import { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { cn, Gallery, NavDots } from '@divlab/divanui';

import { useDeps } from '@Contexts/DI';
import ApiProduct from '@Api/Product';
import useNavigation from '@Navigation/useNavigation';
import * as PickPairStore from '@Stores/PickPair';
import * as ModalWindows from '@Stores/ModalWindows';
import ProductImage from '@Pages/PageProduct/elements/ProductImage';
import useMedias from '@Hooks/useMedias';
import useTranslation from '@Queries/useTranslation';
import ProductTags from '@Components/ProductTags';
import VideoPlayer from '@Components/VideoPlayer';
import OnSaleSoonTab from '@Components/OnSaleSoonTab';
import VideoPreview from '@Components/VideoPreview';
import Button from '@UI/Button';
import Video from '@Components/Video';
import ButtonAr from '../ButtonAr';
import ConstructorImage from '../ConstructorImage';
import LinearTag from '@Promo/elems/LinearTag';
import styles from './PhotoGallery.module.css';

import type { FC, HTMLAttributes, PropsWithChildren } from 'react';
import type { SwiperClass } from '@divlab/divanui';
import type { AR } from '@Types/AR';
import type { ProductData } from '@Types/Product';
import type { TagType, LinearTagData } from '@Promo/elems/LinearTag';
import type { MediaGallery, MediaGalleryItem } from '@Pages/PageProduct/typings';

export interface PhotoGalleryProps extends HTMLAttributes<HTMLDivElement> {
  className?: string;
  images: MediaGallery;
  product: ProductData;
  singleTag?: TagType;
  extraDiscountTag?: LinearTagData;
  comingSoon?: boolean;
  ar?: AR;
}

export interface ItemsProps {
  className?: string;
  onChangeCurrent?(index: number): void;
}

const Items: FC<PropsWithChildren<ItemsProps>> = (props) => {
  const { children, onChangeCurrent, ...restProps } = props;
  const { isMobileM } = useMedias();

  const handleSlideChange = useCallback(
    (swiper: SwiperClass) => {
      onChangeCurrent?.(swiper.realIndex);
    },
    [onChangeCurrent],
  );

  return isMobileM ? (
    <Gallery slidesPerView={1} onSlideChange={handleSlideChange} {...restProps}>
      {children}
    </Gallery>
  ) : (
    <div {...restProps}>{children}</div>
  );
};

const PhotoGallery: FC<PhotoGalleryProps> = (props) => {
  const { ar, images, product, singleTag, comingSoon, extraDiscountTag, ...restProps } = props;
  const { tags = [], id } = product;
  const { productImages } = PickPairStore.useProductInfo();
  const { logger } = useDeps();
  const { isMobileM } = useMedias();
  const { t } = useTranslation();
  const [slideIndex, setSlideIndex] = useState(0);
  const { pathname } = useNavigation();
  const isConstructorPage = pathname.includes('/product/pickpair');
  const [productsOnPins, setProductsOnPins] = useState<ProductData[]>(null);

  const allPhotos = useMemo(() => {
    const firstMedias = images.grid.flat();
    if (!images.other?.length) return firstMedias;

    return firstMedias.concat(images.other);
  }, [images]);

  const allPinProductsIds = useMemo(() => {
    const productIds = [];

    allPhotos.forEach((photo) => {
      (photo.products || []).forEach((pr) => productIds.push(pr.id));
    });

    const uniqProductsIds = Array.from(new Set(productIds));

    return uniqProductsIds;
  }, [allPhotos]);

  const handleClickImage = useCallback(
    (startSlideIndex: number) => {
      if (window.cancelClick) return;

      ModalWindows.open('ProductPhotos', { images: allPhotos, startSlideIndex });
    },
    [allPhotos],
  );

  const handleClickOpenGallery = useCallback(() => {
    ModalWindows.open('ProductPhotos', { images: allPhotos });
  }, [allPhotos]);

  const handleClickVideo = useCallback(() => {
    if (window.cancelClick) return;

    ModalWindows.open('Video', {
      videoId: allPhotos[slideIndex].video,
    });
  }, [allPhotos, slideIndex]);

  const handleChangeCurrent = useCallback((index: number) => {
    setSlideIndex(index);
  }, []);

  const load = useCallback(async () => {
    try {
      if (allPinProductsIds.length === 0) return;

      const res = await ApiProduct.getProducts(allPinProductsIds);

      if (res.length === 0) {
        throw Error("Product wasn't loaded");
      }
      setProductsOnPins(res);
    } catch (err) {
      logger.log(err);
    }
  }, [allPinProductsIds, logger]);

  const renderVideoPreview = (item: MediaGalleryItem) => (
    <VideoPreview
      src={item.image}
      cnPreviewHeight={styles.videoHeight}
      onClick={handleClickVideo}
    />
  );

  const renderSingleImage = (item: MediaGalleryItem, index: number) => {
    const { video, image, title } = item;

    if (video) {
      return image ? (
        <VideoPlayer video={video} preview={image} title={title} />
      ) : (
        <Video src={video} autoPlay loop />
      );
    }

    return (
      <ProductImage
        img={item}
        size='m'
        products={productsOnPins}
        onClickImage={() => handleClickImage(index)}
        loading={index !== 0 ? 'lazy' : 'eager'}
        productId={id}
        className={styles.imageWrapper}
      />
    );
  };

  const renderImages = (item: MediaGalleryItem[] | MediaGalleryItem, index: number) => {
    if (Array.isArray(item)) {
      return item.length > 1 ? (
        <div className={styles.doubleImg}>
          {item.map((img, imgIndex) => (
            <ProductImage
              key={img.title}
              img={img}
              size='m'
              products={productsOnPins}
              onClickImage={() => handleClickImage(index + imgIndex)}
              loading={index !== 0 ? 'lazy' : 'eager'}
              productId={id}
              className={styles.smallImage}
            />
          ))}
        </div>
      ) : (
        renderSingleImage(item[0], index)
      );
    }

    return item.video ? renderVideoPreview(item) : renderSingleImage(item, index);
  };

  useEffect(() => {
    load();
  }, [load]);

  return (
    <div {...restProps} className={cn(styles.photogallery)}>
      <div className={styles.containerImages}>
        {productImages.length > 0 && isConstructorPage && (
          <ConstructorImage images={productImages} />
        )}
        <Items className={styles.items} onChangeCurrent={handleChangeCurrent}>
          {(isMobileM ? allPhotos : images.grid).map(
            (item: MediaGalleryItem | MediaGalleryItem[], index: number) => (
              <div
                className={cn(styles.containerImage, { [styles.firstImage]: index === 0 })}
                key={index}
              >
                {renderImages(item, index)}
                {!isMobileM && index === 0 && ar && (
                  <ButtonAr className={styles.buttonAr} ar={ar} />
                )}
              </div>
            ),
          )}
        </Items>

        {!isMobileM && allPhotos.length > 1 && (
          <div className={styles.footer}>
            <Button
              className={styles.btnOpenGallery}
              theme='transparent'
              onClick={handleClickOpenGallery}
              data-testid='button-open-photomodals'
            >
              {t('ui.open-gallery')}
            </Button>
          </div>
        )}

        {tags.length > 0 && !singleTag && !comingSoon && (
          <ProductTags className={styles.tags} tags={tags} showTagInfo cardView />
        )}
        {singleTag && !comingSoon && (
          <LinearTag
            type={singleTag}
            size='middle'
            className={styles.singleTag}
            animation
            tagData={extraDiscountTag}
          />
        )}
        {comingSoon && <OnSaleSoonTab showIcon className={styles.soonTab} />}
        {isMobileM && ar && <ButtonAr className={styles.buttonAr} ar={ar} />}
        {allPhotos.length > 1 && isMobileM && (
          <div className={styles.wrapperDots}>
            <NavDots count={allPhotos.length} slideIndex={slideIndex} />
          </div>
        )}
      </div>
    </div>
  );
};

export default memo(PhotoGallery);
